import { useEffect } from 'react';

import {
  type GetTopCountryLabelsQuery,
  StoreRegion,
  useGetTopCountryLabelsLazyQuery,
} from '@/lib/graphql/generated/graphql';
import { topCountryLabelsByStoreAtom } from '@/states/country-labels.state';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';

type TopCountryLabelRow = GetTopCountryLabelsQuery['topCountryLabels'][number];

/** Référence stable : évite un nouveau `[]` à chaque rendu (scintillement de la liste pays). */
const EMPTY_TOP_COUNTRY_LABELS: TopCountryLabelRow[] = [];

export function useGetTopCountryLabels(store: StoreRegion) {
  const byStore = useAtomValue(topCountryLabelsByStoreAtom);
  const setByStore = useSetAtom(topCountryLabelsByStoreAtom);

  const [executeFetch, { error }] = useGetTopCountryLabelsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    const existing = getDefaultStore().get(topCountryLabelsByStoreAtom);
    if (existing[store] !== undefined) return; // already loaded for this store
    void executeFetch({ variables: { store } })
      .then((result) => {
        const labels = result.data?.topCountryLabels;
        if (labels) {
          setByStore((prev) => ({ ...prev, [store]: labels }));
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  return {
    countryLabels: byStore[store] ?? EMPTY_TOP_COUNTRY_LABELS,
    loading: byStore[store] === undefined,
    error: byStore[store] === undefined ? error : undefined,
  };
}
